import React, { useState, useEffect, useCallback } from 'react';
import {
  StoreLogo, CartBtn, TabContent, StoreFooter, AWS_URL, FaHeart, InlineEditableImage
} from './ThemeLayoutHelpers';
import {
   MdFilterList, 
  MdDevices, MdSecurity, MdLocalShipping, MdCached, 
  MdEmail, MdPhone, MdLocationOn, MdMenu, MdClose
} from 'react-icons/md';

// Custom InlineEditableTextWithNav component for proper editing and saving
const InlineEditableTextWithNav = ({ p, field, value, className = "", style = {}, children, onNavigate, navPage, isInModal = false }) => {
  const isEditing = p.previewEditMode && p.activeEditField === field;
  const isEditable = typeof p.onActivateField === "function";
  
  // Get the current value from storeTheme first, then from props
  const getCurrentValue = () => {
    if (p.storeTheme && p.storeTheme[field] !== undefined && p.storeTheme[field] !== "") {
      return p.storeTheme[field];
    }
    return value;
  };
  
  const [localValue, setLocalValue] = useState(getCurrentValue());

  // Update local value when storeTheme changes or when editing starts
  useEffect(() => {
    if (!isEditing) {
      setLocalValue(getCurrentValue());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [p.storeTheme, field, value, isEditing]);

  const handleActivate = (event) => {
    event.preventDefault();
    event.stopPropagation();
    const currentVal = getCurrentValue();
    setLocalValue(currentVal);
    if (typeof p.onActivateField === "function") {
      p.onActivateField(field);
    }
  };

  const handleChange = (event) => {
    const newValue = event.target.value;
    setLocalValue(newValue);
  };

  const handleSaveAndClose = () => {
    // Save the current local value to parent
    if (typeof p.onPreviewTextChange === "function") {
      p.onPreviewTextChange(field, localValue);
    }
    // Deactivate edit mode
    if (typeof p.onDeactivateField === "function") {
      p.onDeactivateField();
    }
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      event.stopPropagation();
      handleSaveAndClose();
      // Then navigate if provided and not in modal
      if (!isInModal && onNavigate && navPage) {
        onNavigate(navPage);
      }
    }
  };

  const handleBlur = () => {
    // Save on blur (when clicking outside)
    handleSaveAndClose();
  };

  if (isEditing) {
    const inputClass = "w-full rounded-xl border border-indigo-500 bg-slate-900 text-slate-100 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-indigo-500/20";
    const multiline = field.toLowerCase().includes("subtitle") || field.toLowerCase().includes("description") || field.toLowerCase().includes("overview") || field.toLowerCase().includes("paragraph") || field.toLowerCase().includes("para");

    return multiline ? (
      <textarea
        autoFocus
        rows={4}
        value={localValue}
        onChange={handleChange}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        onClick={(event) => event.stopPropagation()}
        className={inputClass}
        style={{ whiteSpace: "pre-wrap", ...style }}
      />
    ) : (
      <input
        autoFocus
        type="text"
        value={localValue}
        onChange={handleChange}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        onClick={(event) => event.stopPropagation()}
        className={inputClass}
        style={{ whiteSpace: "pre-wrap", ...style }}
      />
    );
  }

  // Display the current value - force re-render when storeTheme changes
  const displayValue = p.storeTheme?.[field] || value;
  
  return (
    <span
      className={`${className} ${isEditable ? 'cursor-pointer inline-block' : 'inline-block'}`}
      style={{ whiteSpace: "pre-wrap", ...style }}
      onClick={isEditable ? handleActivate : undefined}
      title={p.previewEditMode ? "Edit text" : undefined}
    >
      {displayValue}
    </span>
  );
};

export default function NexusLayout({ p }) {
  const T = p.storeTheme || {};
  const primary = T.primaryColor || "#4f46e5"; // Indigo default
  const secondary = T.secondaryColor || "#0ea5e9"; // Sky blue default
  const text = p.storefrontText || {};
  
  const [isScrolled, setIsScrolled] = useState(false);
  const [activePage, setActivePage] = useState("home");
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [activeSpecTab, setActiveSpecTab] = useState("display");
  
  // Slide index for interactive features banner
  const [activeSlide, setActiveSlide] = useState(0);

  // Force re-render when storeTheme changes
  const [refreshKey, setRefreshKey] = useState(0);
  useEffect(() => {
    setRefreshKey(prev => prev + 1);
  }, [p.storeTheme]);

  // Scroll handler for floating navbar
  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 40);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const [contactForm, setContactForm] = useState({ name: "", email: "", message: "" });

  const handleContactSubmit = (e) => {
    e.preventDefault();
    const phoneNumber = p.storeInfo?.phone || "";
    const formattedPhone = phoneNumber.replace(/[^0-9]/g, "");
    if (!formattedPhone) {
      alert("Store phone number is not available.");
      return;
    }
    const textMessage = `Hello, I'm reaching out from your Nexus Storefront:\n\n*Name:* ${contactForm.name}\n*Email:* ${contactForm.email}\n*Message:* ${contactForm.message}`;
    const whatsappUrl = `https://wa.me/${formattedPhone}?text=${encodeURIComponent(textMessage)}`;
    window.open(whatsappUrl, "_blank");
  };


  // Premium Hero sliders (highly visual)
  const techBanners = [
    {
      eyebrow: "Quantum Audio Pro",
      title: "Sound Unleashed. Silence Mastered.",
      subtitle: "Experience studio-grade active noise cancellation with custom-engineered 40mm dynamic drivers and 60 hours of pure battery life.",
      cta: "Explore Audio",
      image: "https://images.unsplash.com/photo-1505740420928-5e560c06d30e?w=1200&q=85",
    },
    {
      eyebrow: "Nexus Smart Display",
      title: "Visual Brilliance. Infinite Control.",
      subtitle: "Transform your home with a vibrant 14-inch OLED display, custom dashboard widgets, and seamless smart home integrations.",
      cta: "Discover Smart Home",
      image: "https://images.unsplash.com/photo-1558002038-1055907df827?w=1200&q=85",
    },
  ];

  // Auto-rotate hero slider - paused in edit mode
  useEffect(() => {
    if (p.previewEditMode) return;
    const interval = setInterval(() => {
      setActiveSlide((prev) => (prev + 1) % techBanners.length);
    }, 6000);
    return () => clearInterval(interval);
  }, [techBanners.length, p.previewEditMode]);

  const navigateTo = useCallback((page, category = null, event) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    if (p.previewEditMode && p.activeEditField) {
      return;
    }
    setActivePage(page);
    setMobileMenuOpen(false);
    if (page === "home") {
      window.scrollTo({ top: 0, behavior: "smooth" });
    } else if (page === "collections" || category) {
      p.setActiveTab("products");
      setTimeout(() => {
        const section = document.getElementById("products-section");
        if (section) section.scrollIntoView({ behavior: "smooth" });
      }, 100);
    } else {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, [p]);

  const handleNavigateAfterEdit = useCallback((page) => {
    setActivePage(page);
    setMobileMenuOpen(false);
    if (page === "home") {
      window.scrollTo({ top: 0, behavior: "smooth" });
    } else if (page === "collections") {
      p.setActiveTab("products");
      setTimeout(() => {
        const section = document.getElementById("products-section");
        if (section) section.scrollIntoView({ behavior: "smooth" });
      }, 100);
    } else {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, [p]);

  return (
    <div 
      className="min-h-screen flex flex-col bg-[#0b0f19] text-slate-200 antialiased" 
      style={{ fontFamily: T.fontFamily || "Poppins, sans-serif" }}
      key={refreshKey}
    >
      {/* ── Top Tech Banner ──────────────────────────────────── */}
      <div 
        className="w-full py-2 px-4 text-center text-xs font-semibold tracking-wider flex items-center justify-center gap-2 border-b border-indigo-950" 
        style={{ 
          background: `linear-gradient(90deg, ${primary}dd, ${secondary}dd)`, 
          color: "#fff" 
        }}
      >
        <span className="animate-pulse">⚡</span>
        <InlineEditableTextWithNav p={p} field="top_bar_message" value="LAUNCH DEALS: Use code NEXUS25 for 25% off all smart devices!" onNavigate={handleNavigateAfterEdit} navPage="home" />
        <span className="hidden md:inline-block border border-white/30 rounded px-1.5 py-0.5 text-[10px] font-bold uppercase ml-2">Limited Time</span>
      </div>

      {/* ── Premium Glassmorphic Header ───────────────────────── */}
      <header 
        className={`sticky top-0 z-40 transition-all duration-300 ${
          isScrolled 
            ? "bg-[#0b0f19]/80 backdrop-blur-xl border-b border-slate-800/80 shadow-2xl py-3" 
            : "bg-transparent border-b border-transparent py-5"
        }`}
      >
        <div className="max-w-7xl mx-auto px-6 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 cursor-pointer" onClick={(e) => navigateTo("home", null, e)}>
            <StoreLogo storeInfo={p.storeInfo} border={`border-2 border-indigo-500/50 shadow-[0_0_15px_rgba(79,70,229,0.3)]`} />
            <div>
              <p className="text-lg font-extrabold bg-gradient-to-r from-white via-slate-100 to-indigo-400 bg-clip-text text-transparent leading-none">
                {p.storeInfo?.businessName || "NEXUS"}
              </p>
              <p className="text-[10px] text-indigo-400 font-semibold tracking-widest uppercase mt-0.5">
                {p.storeInfo?.businessCategory || "Tech Store"}
              </p>
            </div>
          </div>

          {/* Desktop Navigation Links */}
          <nav className="hidden md:flex items-center gap-8">
            {[
              ["home", "Home"],
              ["about", "About Us"],
              ["contact", "Contact"],
            ].map(([page, label]) => (
              <button
                key={page}
                onClick={(e) => navigateTo(page, null, e)}
                className={`text-sm font-semibold tracking-wide transition-all duration-200 relative py-1 hover:text-white ${
                  activePage === page 
                    ? "text-white" 
                    : "text-slate-400"
                }`}
              >
                {label}
                {activePage === page && (
                  <span 
                    className="absolute bottom-0 left-0 right-0 h-0.5 rounded shadow-[0_0_8px_currentColor]"
                    style={{ backgroundColor: primary }}
                  />
                )}
              </button>
            ))}
          </nav>

          {/* Header Controls */}
          <div className="flex items-center gap-3">
            <button 
              onClick={p.handleGoToWishlist} 
              className="relative p-2.5 rounded-xl border border-slate-800 hover:border-slate-700 bg-slate-900/40 text-slate-400 hover:text-red-400 transition-colors"
            >
              <FaHeart className="text-base" />
              {p.wishlistCount > 0 && (
                <span 
                  className="absolute -top-1.5 -right-1.5 h-4 w-4 text-[9px] font-bold rounded-full text-white flex items-center justify-center animate-bounce shadow-lg" 
                  style={{ backgroundColor: primary }}
                >
                  {p.wishlistCount}
                </span>
              )}
            </button>
            <CartBtn p={p} dark />
            {p.userMenuElement}
            
            {/* Mobile Menu Toggle */}
            <button 
              className="md:hidden p-2.5 rounded-xl border border-slate-800 text-slate-400 hover:text-white"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? <MdClose size={20} /> : <MdMenu size={20} />}
            </button>
          </div>
        </div>

        {/* Mobile Navigation Drawer */}
        {mobileMenuOpen && (
          <div className="absolute top-full left-0 right-0 bg-[#0d1326] border-b border-slate-800 shadow-2xl p-6 flex flex-col gap-4 animate-fadeIn md:hidden">
            {[
              ["home", "Home"],
              ["about", "About Us"],
              ["contact", "Contact"],
            ].map(([page, label]) => (
              <button
                key={page}
                onClick={(e) => navigateTo(page, null, e)}
                className={`py-2 text-left text-base font-semibold border-b border-slate-900 hover:text-white ${
                  activePage === page ? "text-indigo-400" : "text-slate-400"
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        )}
      </header>

      {/* ── Page Content ───────────────────────────────────────── */}
      <main className="flex-grow">
        {activePage === "home" && (
          <>
            {/* ── Hero Banner Section (Interactive Slider) ───────── */}
            <section className="max-w-7xl mx-auto px-6 pt-4 pb-8">
              <div className="relative rounded-3xl overflow-hidden shadow-[0_20px_60px_rgba(79,70,229,0.15)] bg-slate-950 border border-slate-800/60">
                {/* Glowing radial backdrop */}
                <div 
                  className="absolute -top-40 -left-40 w-96 h-96 rounded-full blur-[140px] opacity-40 pointer-events-none"
                  style={{ backgroundColor: primary }}
                />
                <div 
                  className="absolute -bottom-40 -right-40 w-96 h-96 rounded-full blur-[140px] opacity-40 pointer-events-none"
                  style={{ backgroundColor: secondary }}
                />

                <div className="relative min-h-[460px] md:min-h-[520px] flex flex-col md:flex-row items-center">
                  {/* Left Column - Hero Content */}
                  <div className="flex-1 p-8 md:p-14 z-10 text-left">
                    <span 
                      className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-[0.25em] px-3.5 py-1.5 rounded-full border mb-5 shadow-inner"
                      style={{ 
                        borderColor: `${primary}50`, 
                        backgroundColor: `${primary}10`,
                        color: secondary
                      }}
                    >
                      <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                      <InlineEditableTextWithNav p={p} field={`heroEyebrow_${activeSlide}`} value={techBanners[activeSlide].eyebrow} onNavigate={handleNavigateAfterEdit} navPage="home" />
                    </span>
                    <h1 className="text-4xl md:text-6xl font-black text-white leading-tight tracking-tight">
                      <InlineEditableTextWithNav p={p} field={`heroTitle_${activeSlide}`} value={techBanners[activeSlide].title} onNavigate={handleNavigateAfterEdit} navPage="home" />
                    </h1>
                    <p className="mt-4 text-slate-400 text-sm md:text-base max-w-lg leading-relaxed">
                      <InlineEditableTextWithNav p={p} field={`heroSubtitle_${activeSlide}`} value={techBanners[activeSlide].subtitle} onNavigate={handleNavigateAfterEdit} navPage="home" />
                    </p>
                    <div className="mt-8 flex flex-wrap gap-4">
                      <button 
                        onClick={() => {
                          const section = document.getElementById("products-section");
                          if (section) section.scrollIntoView({ behavior: "smooth" });
                        }}
                        className="px-8 py-4 rounded-2xl text-sm font-bold text-white shadow-xl hover:shadow-2xl hover:scale-105 active:scale-95 transition-all duration-200"
                        style={{ background: `linear-gradient(135deg, ${primary}, ${secondary})` }}
                      >
                        <InlineEditableTextWithNav p={p} field={`heroCta_${activeSlide}`} value={techBanners[activeSlide].cta} onNavigate={handleNavigateAfterEdit} navPage="home" />
                      </button>
                      <button 
                        onClick={() => navigateTo("about")}
                        className="px-8 py-4 rounded-2xl text-sm font-semibold text-slate-300 border border-slate-800 bg-slate-900/50 hover:bg-slate-900 hover:border-slate-700 transition-colors"
                      >
                        Learn More
                      </button>
                    </div>
                  </div>

                  {/* Right Column - Hero Visual */}
                  <div className="flex-1 w-full h-[260px] md:h-[520px] relative overflow-hidden self-stretch shrink-0">
                    <InlineEditableImage p={p} field={`nexus_slide_${activeSlide}_image`} className="h-full w-full">
                      <img 
                        src={p.storeTheme?.[`nexus_slide_${activeSlide}_image`] || techBanners[activeSlide].image} 
                        alt="Premium Tech" 
                        className="w-full h-full object-cover transition-all duration-1000 transform hover:scale-105"
                      />
                    </InlineEditableImage>
                    {/* Dark gradient fade overlay */}
                    <div className="absolute inset-0 bg-gradient-to-t md:bg-gradient-to-r from-slate-950 via-transparent to-transparent pointer-events-none" />
                  </div>
                </div>

                {/* Slider Controls */}
                <div className="absolute bottom-6 left-8 md:left-14 flex items-center gap-3 z-20">
                  {techBanners.map((_, idx) => (
                    <button
                      key={idx}
                      onClick={() => setActiveSlide(idx)}
                      className={`h-2.5 rounded-full transition-all duration-300 ${
                        activeSlide === idx ? "w-8 bg-indigo-500" : "w-2.5 bg-slate-700 hover:bg-slate-500"
                      }`}
                    />
                  ))}
                </div>
              </div>
            </section>

            {/* ── Premium Tech Categories / Badges ───────────────── */}
            <section className="max-w-7xl mx-auto px-6 py-6">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {[
                  { label: "Premium Devices", desc: "Top Brand Electronics", icon: <MdDevices className="text-2xl text-cyan-400" /> },
                  { label: "Secure Payment", desc: "100% Encrypted Transactions", icon: <MdSecurity className="text-2xl text-indigo-400" /> },
                  { label: "Express Shipping", desc: "Next Day Delivery Available", icon: <MdLocalShipping className="text-2xl text-purple-400" /> },
                  { label: "Warranty Guard", desc: "Hassle-Free Replacement", icon: <MdCached className="text-2xl text-emerald-400" /> },
                ].map((item, idx) => (
                  <div 
                    key={idx} 
                    className="p-5 rounded-2xl bg-slate-900/30 border border-slate-800/40 hover:border-slate-800 transition-colors flex items-center gap-4 text-left"
                  >
                    <div className="p-3.5 rounded-xl bg-slate-950 border border-slate-800">
                      {item.icon}
                    </div>
                    <div>
                      <p className="text-sm font-bold text-white">{item.label}</p>
                      <p className="text-xs text-slate-500 mt-0.5">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            {/* ── Interactive Spec Spotlight Section (Apple-like) ──── */}
            <section className="max-w-7xl mx-auto px-6 py-12">
              <div className="p-8 md:p-14 rounded-3xl bg-gradient-to-b from-slate-900/50 to-slate-955/20 border border-slate-800/50 relative overflow-hidden">
                <div 
                  className="absolute right-0 top-0 w-80 h-80 rounded-full blur-[130px] opacity-25 pointer-events-none"
                  style={{ backgroundColor: secondary }}
                />
                
                <div className="max-w-xl text-left mb-10">
                  <span className="text-xs font-bold uppercase tracking-wider text-indigo-400">Innovation First</span>
                  <h2 className="text-3xl md:text-4xl font-extrabold text-white mt-1">Designed for high performance.</h2>
                  <p className="text-slate-400 text-sm mt-3">We engineer devices with ultra-modern specifications to keep you ahead. Explore the core technology architecture powering Nexus products.</p>
                </div>

                <div className="flex flex-col md:flex-row gap-8 items-start">
                  {/* Tabs */}
                  <div className="flex md:flex-col gap-2 overflow-x-auto w-full md:w-48 pb-2 md:pb-0 border-b md:border-b-0 md:border-r border-slate-800 shrink-0">
                    {[
                      { id: "display", label: "OLED Display" },
                      { id: "processor", label: "Processor & SoC" },
                      { id: "battery", label: "Battery Technology" },
                    ].map((tab) => (
                      <button
                        key={tab.id}
                        onClick={() => setActiveSpecTab(tab.id)}
                        className={`px-4 py-2.5 rounded-xl text-sm font-bold text-left whitespace-nowrap transition-colors ${
                          activeSpecTab === tab.id 
                            ? "bg-slate-900 border-l-2 text-white" 
                            : "text-slate-400 hover:text-white"
                        }`}
                        style={activeSpecTab === tab.id ? { borderLeftColor: primary } : {}}
                      >
                        {tab.label}
                      </button>
                    ))}
                  </div>

                  {/* Tab Display Panel */}
                  <div className="flex-1 text-left bg-slate-950/60 p-6 md:p-8 rounded-2xl border border-slate-800/40">
                    {activeSpecTab === "display" && (
                      <div className="animate-fadeIn">
                        <h4 className="text-lg font-bold text-white mb-2">120Hz Liquid Retina XDR</h4>
                        <p className="text-slate-400 text-sm leading-relaxed mb-4">Featuring a custom mini-LED backlight system delivering 1,600 nits of peak brightness and a 1,000,000:1 contrast ratio. Experience pitch blacks, vibrant colors, and ultra-smooth scrolling speeds.</p>
                        <div className="grid grid-cols-2 gap-4 mt-6">
                          <div className="p-4 rounded-xl bg-slate-900/40 border border-slate-800">
                            <span className="text-2xl font-black text-white">1600nits</span>
                            <p className="text-xs text-slate-500 mt-1">Peak Brightness</p>
                          </div>
                          <div className="p-4 rounded-xl bg-slate-900/40 border border-slate-800">
                            <span className="text-2xl font-black text-white">120Hz</span>
                            <p className="text-xs text-slate-500 mt-1">ProMotion Technology</p>
                          </div>
                        </div>
                      </div>
                    )}
                    {activeSpecTab === "processor" && (
                      <div className="animate-fadeIn">
                        <h4 className="text-lg font-bold text-white mb-2">Neural Core M3 Chipset</h4>
                        <p className="text-slate-400 text-sm leading-relaxed mb-4">Built on a cutting-edge 3nm architecture. Packed with 20 billion transistors, a 10-core GPU, and an advanced 16-core Neural Engine designed for next-generation hardware machine learning processing.</p>
                        <div className="grid grid-cols-2 gap-4 mt-6">
                          <div className="p-4 rounded-xl bg-slate-900/40 border border-slate-800">
                            <span className="text-2xl font-black text-white">3nm</span>
                            <p className="text-xs text-slate-500 mt-1">Lithography SoC</p>
                          </div>
                          <div className="p-4 rounded-xl bg-slate-900/40 border border-slate-800">
                            <span className="text-2xl font-black text-white">16 Cores</span>
                            <p className="text-xs text-slate-500 mt-1">Neural AI Engine</p>
                          </div>
                        </div>
                      </div>
                    )}
                    {activeSpecTab === "battery" && (
                      <div className="animate-fadeIn">
                        <h4 className="text-lg font-bold text-white mb-2">Solid-State Battery Matrix</h4>
                        <p className="text-slate-400 text-sm leading-relaxed mb-4">Engineered using safe, solid electrolyte matrix configurations. Delivers 2.5x energy density compared to regular lithium-ion batteries. Includes a high-power 120W charge integration that fills 80% in 15 mins.</p>
                        <div className="grid grid-cols-2 gap-4 mt-6">
                          <div className="p-4 rounded-xl bg-slate-900/40 border border-slate-800">
                            <span className="text-2xl font-black text-white">120W</span>
                            <p className="text-xs text-slate-500 mt-1">HyperCharge Speed</p>
                          </div>
                          <div className="p-4 rounded-xl bg-slate-900/40 border border-slate-800">
                            <span className="text-2xl font-black text-white">60 Hours</span>
                            <p className="text-xs text-slate-500 mt-1">Maximum Playtime</p>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </section>

            {/* ── Main Catalog / Products Grid ────────────────────── */}
            <section id="products-section" className="max-w-7xl mx-auto px-6 py-12 scroll-mt-24">
              <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-8">
                <div className="text-left">
                  <span className="text-xs font-bold uppercase tracking-widest text-indigo-400">Store Catalog</span>
                  <h3 className="text-3xl font-extrabold text-white mt-1">Innovative Hardware</h3>
                </div>

                {/* Filter and Tab Panel Toggle */}
                <div className="flex items-center gap-2.5">
                  <button 
                    onClick={() => p.setFilterPanelOpen(true)} 
                    className="flex items-center gap-1.5 px-4 py-2.5 rounded-xl border border-slate-800 hover:border-slate-700 bg-slate-900/30 text-sm font-semibold text-slate-300 transition-colors"
                  >
                    <MdFilterList size={18} /> {text.filter || "Filter"}
                  </button>
                  <div className="rounded-xl border border-slate-800 bg-slate-950 p-1 flex gap-1">
                    {[
                      ["products", text.products || "Shop"],
                      ["services", text.services || "Services"],
                    ].map(([k, l]) => (
                      <button
                        key={k}
                        onClick={() => p.setActiveTab(k)}
                        className="px-4 py-1.5 rounded-lg text-xs font-bold transition-colors whitespace-nowrap"
                        style={p.activeTab === k ? { backgroundColor: primary, color: "#fff" } : { color: "#94a3b8" }}
                      >
                        {l}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* The main catalog grid */}
              <div className="w-full">
                <TabContent p={p} dark />
              </div>
            </section>

            {/* ── Newsletter Subscription Box ───────────────────────── */}
            <section className="max-w-7xl mx-auto px-6 py-12">
              <div className="relative p-8 md:p-14 rounded-3xl bg-slate-950 border border-slate-800 text-center overflow-hidden">
                <div 
                  className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] rounded-full blur-[140px] opacity-10 pointer-events-none"
                  style={{ backgroundColor: primary }}
                />
                
                <h3 className="text-2xl md:text-3xl font-black text-white">Join the Nexus Community</h3>
                <p className="text-slate-400 text-sm max-w-md mx-auto mt-2.5">Subscribe to receive launch notifications, access to beta software, and premium member-only deals.</p>
                
                <form 
                  onSubmit={(e) => { e.preventDefault(); alert("Subscribed!"); }}
                  className="max-w-md mx-auto mt-8 flex flex-col sm:flex-row gap-3 relative z-10"
                >
                  <div className="flex-grow relative">
                    <MdEmail className="absolute left-4.5 top-1/2 -translate-y-1/2 text-slate-500 text-lg" />
                    <input 
                      type="email" 
                      required 
                      placeholder="Enter your email address" 
                      className="w-full pl-11 pr-4 py-3.5 rounded-2xl bg-slate-900 border border-slate-800 text-slate-200 text-sm focus:outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 placeholder-slate-600"
                    />
                  </div>
                  <button 
                    type="submit" 
                    className="px-6 py-3.5 rounded-2xl text-sm font-bold text-white shadow-xl hover:opacity-90 active:scale-95 transition-all"
                    style={{ backgroundColor: primary }}
                  >
                    Subscribe
                  </button>
                </form>
              </div>
            </section>
          </>
        )}

        {activePage === "about" && (
          <section className="max-w-4xl mx-auto px-6 py-16 text-left">
            <h2 className="text-4xl font-extrabold text-white tracking-tight mb-4">
              <InlineEditableTextWithNav p={p} field="about_title" value="Our Innovation Story" onNavigate={handleNavigateAfterEdit} navPage="about" />
            </h2>
            <div className="h-1 w-20 rounded mb-8" style={{ backgroundColor: primary }} />
            
            <div className="prose prose-invert max-w-none text-slate-300 space-y-6 text-base leading-relaxed">
              <p>
                <InlineEditableTextWithNav p={p} field="about_para1" value={p.storeDescription || p.storeInfo?.businessDescription || "At Nexus, our goal is to design technology products that blend high-performance specs with minimal design styles. We believe that hardware should be powerful yet incredibly simple to use, offering real solutions for our customers' digital needs."} onNavigate={handleNavigateAfterEdit} navPage="about" />
              </p>
              
              <div className="my-10 rounded-2xl overflow-hidden border border-slate-800">
                <img 
                  src="https://images.unsplash.com/photo-1519389950473-47ba0277781c?w=1000&q=80" 
                  alt="Our Tech Team" 
                  className="w-full h-64 object-cover"
                />
              </div>

              <p>
                <InlineEditableTextWithNav p={p} field="about_para2" value="Founded in 2024, our engineers work alongside industrial designers to construct devices with premium internals. From solid-state battery matrices to high-contrast Liquid Retina displays, every detail is engineered with absolute precision." onNavigate={handleNavigateAfterEdit} navPage="about" />
              </p>
              
              <div className="p-6 rounded-2xl bg-slate-900/40 border border-slate-800 my-8">
                <p className="font-bold text-white text-lg">💡 Our Philosophy</p>
                <p className="text-slate-400 mt-2 text-sm">"Simplicity is the ultimate sophistication. When high performance resides in beautiful, minimalist design, true innovation is achieved."</p>
              </div>
            </div>
          </section>
        )}

        {activePage === "contact" && (
          <section className="max-w-7xl mx-auto px-6 py-16 text-left">
            <h2 className="text-4xl font-extrabold text-white tracking-tight mb-4">Contact Nexus</h2>
            <div className="h-1 w-20 rounded mb-10" style={{ backgroundColor: primary }} />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
              {/* Contact Info */}
              <div>
                <h3 className="text-2xl font-bold text-white mb-6">Get In Touch</h3>
                <p className="text-slate-400 text-sm mb-8 leading-relaxed">Have questions about our smart displays, audio gear, or custom setup services? Reach out to our specialized support staff. We're here to help.</p>
                
                <div className="space-y-6">
                  <div className="flex items-center gap-4">
                    <div className="p-3.5 rounded-xl bg-slate-900 border border-slate-800 text-indigo-400">
                      <MdPhone size={20} />
                    </div>
                    <div>
                      <p className="text-xs text-slate-500">Phone Support</p>
                      <p className="text-sm font-bold text-white mt-0.5">
                        <InlineEditableTextWithNav p={p} field="contact_phone" value="+1 (555) 892-NEXUS" onNavigate={handleNavigateAfterEdit} navPage="contact" />
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-4">
                    <div className="p-3.5 rounded-xl bg-slate-900 border border-slate-800 text-cyan-400">
                      <MdEmail size={20} />
                    </div>
                    <div>
                      <p className="text-xs text-slate-500">Email Address</p>
                      <p className="text-sm font-bold text-white mt-0.5">
                        <InlineEditableTextWithNav p={p} field="contact_email" value="support@nexus-store.com" onNavigate={handleNavigateAfterEdit} navPage="contact" />
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-4">
                    <div className="p-3.5 rounded-xl bg-slate-900 border border-slate-800 text-purple-400">
                      <MdLocationOn size={20} />
                    </div>
                    <div>
                      <p className="text-xs text-slate-500">Headquarters</p>
                      <p className="text-sm font-bold text-white mt-0.5">
                        <InlineEditableTextWithNav p={p} field="contact_address" value="Innovation District, Suite 500, San Francisco, CA" onNavigate={handleNavigateAfterEdit} navPage="contact" />
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Contact Form */}
              <div className="p-8 rounded-3xl bg-slate-950 border border-slate-800">
                <h4 className="text-lg font-bold text-white mb-6">Send A Message</h4>
                <form onSubmit={handleContactSubmit} className="space-y-4">
                  <div>
                    <label className="block text-xs font-semibold text-slate-400 mb-1.5">Full Name</label>
                    <input 
                      type="text" 
                      required 
                      value={contactForm.name}
                      onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })}
                      className="w-full bg-slate-900 border border-slate-800 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-indigo-500 text-slate-200" 
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-slate-400 mb-1.5">Email Address</label>
                    <input 
                      type="email" 
                      required 
                      value={contactForm.email}
                      onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
                      className="w-full bg-slate-900 border border-slate-800 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-indigo-500 text-slate-200" 
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-slate-400 mb-1.5">Message</label>
                    <textarea 
                      rows={4} 
                      required 
                      value={contactForm.message}
                      onChange={(e) => setContactForm({ ...contactForm, message: e.target.value })}
                      className="w-full bg-slate-900 border border-slate-800 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-indigo-500 text-slate-200" 
                    />
                  </div>
                  <button 
                    type="submit" 
                    className="w-full py-3.5 rounded-xl text-sm font-bold text-white transition-opacity hover:opacity-90"
                    style={{ backgroundColor: primary }}
                  >
                    Send Message via WhatsApp
                  </button>
                </form>
              </div>
            </div>
          </section>
        )}
      </main>

      {/* ── Footer ────────────────────────────────────────────── */}
      <StoreFooter p={p} dark />
    </div>
  );
}
